import { useEffect, useRef, useState } from 'react';
import Slider from 'react-slick';

import Arrow from '../../../assets/about/about-arrow.svg';
import arrowLeft from '../../../assets/about/carousel-arrow-left.svg';
import arrowRight from '../../../assets/about/carousel-arrow-right.svg';
import ScrollInAnimationItem from '../../../components/scrollInAnimationItem/ScrollInAnimationItem';
import { ScrollAnimationType } from '../../../enums/scroll-animation-type.enum';
import { useElementOnScreen } from '../../../hooks/useElementOnScreen';
import { useWindowSize } from '../../../hooks/useWindowSize';
import AboutCreatorCard from '../aboutCreatorCard/AboutCreatorCard';
import { ABOUT_CREATORS } from '../constants/about-creators.constants';

import styles from './AboutCreatorsSection.module.scss';

const AboutCreatorsSection = () => {
  const slideWidth = 316;
  const slidePadding = 12;
  const carouselRef = useRef<Slider>(null);
  const carouselContainerRef = useRef<HTMLDivElement>(null);
  const [width] = useWindowSize();
  const [visibleSlides, setVisibleSlides] = useState(5);
  const [creators, setCreators] = useState(ABOUT_CREATORS);
  const onScreen = useElementOnScreen(carouselContainerRef, 0);

  useEffect(() => {
    let timeout: NodeJS.Timeout;

    if (onScreen) {
      timeout = setTimeout(() => carouselRef.current?.slickPlay(), 0);
    } else {
      timeout = setTimeout(() => carouselRef.current?.slickPause(), 0);
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout);
      }
    };
  }, [carouselRef, onScreen]);

  useEffect(() => {
    const slidesCount = Math.floor(width / (slideWidth + slidePadding));
    const remainingSpace = width / (slideWidth + slidePadding) - slidesCount;

    const hasEnoughSpace = slidesCount % 2 !== 0 || remainingSpace > 0.7;
    const slidesShift = hasEnoughSpace ? Math.floor(slidesCount / 2) : Math.floor(slidesCount / 2) - 1;
    let shiftedSlides: any = [];
    let remainingSlides: any = [];

    ABOUT_CREATORS.forEach((creator, index) => {
      if (index < slidesShift) {
        shiftedSlides = [...shiftedSlides, creator];
      } else {
        remainingSlides.push(creator);
      }
    });

    setCreators([...remainingSlides, ...shiftedSlides]);

    setVisibleSlides(slidesCount);
  }, [width]);

  return (
    <div className={styles['section']}>
      <div className={styles['content']}>
        <ScrollInAnimationItem animationType={ScrollAnimationType.slideInTop} delay="0.1s" threshold={0.3}>
          <h2>Creators</h2>
        </ScrollInAnimationItem>
        <ScrollInAnimationItem
          className={styles['arrow']}
          animationType={ScrollAnimationType.slideInLeft}
          delay="0.2s"
          threshold={0.3}
        >
          <img alt="" src={Arrow} />
        </ScrollInAnimationItem>
        <ScrollInAnimationItem delay="0.3s" threshold={0.3}>
          <p className={styles['description']}>Combined knowledge of game development and blockchain</p>
        </ScrollInAnimationItem>
      </div>
      <div className={styles['carousel-container']} ref={carouselContainerRef}>
        {visibleSlides < creators.length && (
          <button
            className={`button--styleless ${styles['carousel-button--left']}`}
            onClick={() => carouselRef.current?.slickPrev()}
          >
            <img className={styles['carousel-arrow']} src={arrowLeft} alt="Slide left" />
          </button>
        )}
        <Slider
          autoplay
          centerMode
          dots
          infinite
          variableWidth
          speed={300}
          autoplaySpeed={3000}
          slidesToScroll={1}
          centerPadding="0"
          dotsClass={`slick-dots ${styles['carousel-dots']}`}
          slidesToShow={visibleSlides}
          ref={carouselRef}
          useTransform={false}
        >
          {creators.map((creator) => (
            <AboutCreatorCard
              key={creator.name}
              imageUrl={creator.image}
              name={creator.name}
              position={creator.position}
              link={creator.link}
            />
          ))}
        </Slider>
        {visibleSlides < creators.length && (
          <button
            className={`button--styleless ${styles['carousel-button--right']}`}
            onClick={() => carouselRef.current?.slickNext()}
          >
            <img className={styles['carousel-arrow']} src={arrowRight} alt="Slide right" />
          </button>
        )}
      </div>
    </div>
  );
};

export default AboutCreatorsSection;
